import {
  Box,
  FormControl,
  TextField,
  styled,
  MenuItem,
  Typography,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import InputField from "./InputField";
import { updatePosition } from "../../utils/updatePosition";
import { get } from "../../utils/api";

// CustomTextField styled to match input style
// const CustomTextField = styled(TextField)(({ theme, error }) => ({
const CustomTextField = styled(TextField, {
  shouldForwardProp: (prop) =>
    prop !== "height" && prop !== "error" && prop !== "disabledGrey",
})(({ theme, error, height, disabledGrey }) => ({
  "& .MuiInputBase-root": {
    height: height || "40px",
  },
  ...(disabledGrey && {
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: "#6b7280",
      color: "#6b7280",
      cursor: "not-allowed",
    },
    "& .MuiInputBase-root.Mui-disabled": {
      backgroundColor: "#f3f4f6",
      cursor: "not-allowed",
    },
  }),
  ...(error && {
    "& .MuiOutlinedInput-root": {
      borderColor: "#D34040",
      "&:hover": {
        borderColor: "#D34040",
      },
      "&.Mui-focused": {
        borderColor: "#D34040",
      },
    },
  }),
}));

const SelectField = ({
  defaultValue,
  newOption,
  title,
  value,
  handleChange,
  name,
  options,
  errors,
  height,
  url,
  field,
  keyName,
  disable,
  menuDisable,
  marginTop,
  isAddedNew,
  bgColor,
  width,
  mt,
  labelMinHeight,
  disabledGrey,
  /** When true, no title label is rendered above the select (inline row layouts). */
  hideTitle = false,
  /** When true, empty string is a valid value and placeholder menu row uses value="" (clearable select). */
  allowClear = false,
  /** When true, validation message is not shown under the field (parent can render errors). */
  hideErrorTypography = false,
}) => {
  const [newOptions, setNewOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newOptionValue, setNewOptionValue] = useState("");

  // Styled MenuItem for ellipsis with text wrapping
  const EllipsisMenuItem = styled(MenuItem)(({ theme }) => ({
    width: "100%",
    overflow: "hidden",
    whiteSpace: "normal", // Allow text to wrap
    textOverflow: "clip", // No text overflow, allow wrap
    maxWidth: "none",
    wordBreak: "break-word", // Ensures that long words break correctly and wrap to the next line
    boxSizing: "border-box",
  }));

  // Fetch options
  const fetchOptions = async () => {
    setLoading(true);
    try {
      const res = await get(url);
      if (name === "partnerCompany") {
        if (
          Array.isArray(res.data.companies) &&
          res.data.companies.length > 0
        ) {
          const fetchedOptions = res.data.companies.map((item) => ({
            id: item._id,
            name: field ? item[field] : item.companyName || item.facilityName,
          }));
          setNewOptions(fetchedOptions);
        }
      } else if (name === "assignedTo") {
        if (Array.isArray(res.data.users) && res.data.users.length > 0) {
          const fetchedOptions = res.data.users.map((item) => ({
            id: item._id,
            name: item.name,
          }));
          setNewOptions(fetchedOptions);
        }
      } else {
        if (Array.isArray(res.data.data) && res.data.data.length > 0) {
          const fetchedOptions = res.data.data.map((item) => ({
            id: item._id,
            name: field
              ? item[field]
              : item.name || item.facilityName || item.name,
          }));
          setNewOptions(fetchedOptions);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAddedNew) {
      setIsAddingNew(true);
      setNewOptionValue(value);
    }
    if (url) {
      fetchOptions();
    }

    console.log("url", url,options);
  }, []);

  // Customize MenuProps to control dropdown behavior
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: "55%",
        overflowY: "auto",
        bgColor: bgColor || "inherit",
      },
    },
  };

  // Handle adding new option
  const handleAddNew = (e) => {
    console.log("adding", e.target.value);
    setNewOptionValue(e.target.value);
    handleChange({ target: { name, value: e.target.value } }); // Automatically select the new option
  };

  // Combine static options with new ones
  const allOptions =
    newOption || newOptions
      ? updatePosition([
          ...(options || []), // Spread the existing options, fallback to empty array if undefined
          ...(newOptions || []), // Spread the fetched options
          ...(newOption ? [newOption] : []), // Add the new option value if it's not empty
        ])
      : options || newOptions;

  return (
    <Box>
      {!hideTitle ? (
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: "bold",
            mt: mt ? mt : name === "consumptionPeriod" ? marginTop || 0 : 1,
            minHeight: labelMinHeight || "auto",
          }}
        >
          {title}
        </Typography>
      ) : null}
      <FormControl
        margin={hideTitle ? "none" : "normal"}
        sx={{ mt: 0, width: width ? width : "100%" }}
      >
        <CustomTextField
          select
          height={height}
          disabledGrey={disabledGrey}
          disabled={disable}
          name={name}
          value={
            isAddedNew || isAddingNew
              ? "Others"
              : allowClear
                ? value || ""
                : value || "Tap to Select"
          }
          onChange={(e) => {
            handleChange(e);
            e.target.value !== "Others" && setIsAddingNew(false);
          }}
          error={!!errors}
          SelectProps={{
            MenuProps,
            ...(allowClear && {
              displayEmpty: true,
              renderValue: (selected) =>
                selected ? selected : defaultValue || "Tap to Select",
            }),
          }}
        >
          <MenuItem value={allowClear ? "" : "Tap to Select"} disabled={!allowClear}>
            {isAddingNew ? "Other (specify)" : defaultValue ? defaultValue : "Tap to Select"}
          </MenuItem>

          {loading ? (
            <MenuItem disabled>
              <CircularProgress size={24} />
            </MenuItem>
          ) : (
            allOptions.map((option, index) => {
              return option === "Others" ? (
                <EllipsisMenuItem
                  key={index}
                  value="Others"
                  onClick={() => {
                    setIsAddingNew(true);
                    setNewOptionValue(""); // Clear any previous value
                  }}
                >
                  Other (specify)
                </EllipsisMenuItem>
              ) : (
                <EllipsisMenuItem
                  key={option?.id || index}
                  disabled={option === menuDisable}
                  value={option?.id || option?._id || option?.name || option?.label || option}
                >
                  {option?.[field] ||
                    option?.name ||
                    option?.month ||
                    option?.vehicleNumber ||
                    option?.driverName ||
                    option?.label ||
                    option}{" "}
                  {/* Render the option's name */}
                </EllipsisMenuItem>
              );
            })
          )}
        </CustomTextField>

        {/* Add New Option Input */}
        {isAddingNew && (
          <Box sx={{ mt: 0.5 }}>
            <InputField
              value={newOptionValue}
              handleChange={handleAddNew}
              label="New Option"
              // fullWidth
              placeholder="Enter New Option"
            />
            {/* <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}
            >
              <Button onClick={() => setIsAddingNew(false)} variant="contained"
              color="secondary" sx={{
                // backgroundColor: "#0a3f73",
                textTransform: "none",
                marginTop: "3%",
              }}>
                Cancel
              </Button> */}
            {/* <Button
              variant="contained"
              color="primary"
              onClick={handleAddNew}
              sx={{
                backgroundColor: "#0a3f73",
                textTransform: "none",
                marginTop: "3%",
              }}
              // disabled={loading}
            >
                Add
              </Button> */}
            {/* </Box> */}
          </Box>
        )}

        {errors && !hideErrorTypography && (
          <Typography
            variant="body2"
            color="#D34040"
            sx={{
              fontFamily:
                'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
              fontSize: "0.875rem",
              fontWeight: 400,
              lineHeight: 1.43,
              letterSpacing: "0.01786em",
              mt: "3px",
            }}
          >
            {errors}
          </Typography>
        )}
      </FormControl>
    </Box>
  );
};

export default SelectField;
